<template>
  <div
    v-if="
      paymentInfo.businessEntity &&
        paymentInfo.employee &&
        (videoPlayed || !paymentInfo.businessEntity.presentationMode)
    "
    v-loading.fullscreen.lock="fullScreenLoading"
    class="payment-page"
  >
    <div
      v-show="!fullScreenLoading"
      class="header"
      :style="{ background: schemaPrimaryColor }"
    >
      <el-row type="flex">
        <div class="header-logo">
          <el-image
            class="header-logo-img"
            :src="profileImage"
            :preview-src-list="[profileImage]"
          />
        </div>
        <div>
          <p
            v-if="paymentInfo.businessEntity.name"
            class="header-toptitle"
          >
            {{ paymentInfo.businessEntity.name }}
          </p>
          <h3 class="header-title">
            {{ title }}
          </h3>
          <p
            v-if="!showTeamName && paymentInfo.motivationQuote"
            class="header-subtitle"
          >
            {{ paymentInfo.motivationQuote }}
          </p>
        </div>
      </el-row>
    </div>
    <div
      class="body"
      v-show="!fullScreenLoading"
    >
      <el-row>
        <defined-tips-percentage
          v-if="paymentInfo.amount"
          :elements="paymentInfo.team.defTipsPercentage"
          :model.sync="totalAmount"
          :currency="paymentInfo.currency"
          :amount="Number(paymentInfo.amount)"
          :primary-color="schemaPrimaryColor"
        />
        <defined-tips-amount
          v-else
          :elements="paymentInfo.team.defTipsAmount"
          :model.sync="totalAmount"
          :currency="paymentInfo.currency"
          :primary-color="schemaPrimaryColor"
          :presentation-mode="paymentInfo.businessEntity.presentationMode"
        />
      </el-row>
      <el-row class="body-amount-text">
        <p>{{ $t("pay.input-amount-text") }}</p>
      </el-row>
      <el-row class="body-amount-input">
        <currency-input
          :presentation-mode="
            paymentInfo &&
              paymentInfo.businessEntity &&
              paymentInfo.businessEntity.presentationMode
          "
          v-model="totalAmount"
          :options="currencyInputOptions"
          @input="inputAmount"
          :style="{
            '--schema-color': schemaPrimaryColor,
          }"
        />
      </el-row>
      <el-row
        v-if="
          paymentInfo.businessEntity.feePercentage > 0 &&
            totalPay > 0 &&
            !paymentInfo.businessEntity.waiterPaysFee
        "
      >
        <fee-checkbox
          v-model="payFee"
          :currency="paymentInfo.currency"
          :strategy="payGuest"
          :total-amount.sync="totalAmount"
          :fee-percentage="paymentInfo.businessEntity.feePercentage"
          :fee-amount.sync="paymentInfo.feeAmount"
          :payment-for="paymentFor"
          :primary-color="schemaPrimaryColor"
        />
      </el-row>
      <el-row
        v-if="totalPay >= minAmount"
        class="body-total-amount"
      >
        <span>
          {{ $t("pay.total-amount") }}:
          {{
            (totalPay / 100).toLocaleString($localeString, {
              style: "currency",
              currency: paymentInfo.currency,
            })
          }}
        </span>
      </el-row>
      <el-row class="body-payment-methods">
        <div
          @click="showTermsWarning"
          class="terms-unchecked-warning"
          v-if="isPlanzer && !termsAgree"
        >
          <p v-if="isTermsWarning">
            {{ $t("pay.planzer-terms") }}
          </p>
        </div>
        <PaymentMethods
          :class="{ 'is-terms-warnings': isTermsWarning && !termsAgree }"
          v-if="totalPay >= minAmount"
          :client-pays-fee="payFee"
          :terms-agree="termsAgree"
          :total-amount="totalPay"
          :tip-amount="totalAmount"
          :currency="paymentInfo.currency"
          :qr-codes="[paymentInfo.qrCode]"
          :business="paymentInfo.businessEntity"
          :employee="paymentInfo.employee"
          :team="paymentInfo.team"
          :payment-object="paymentObject"
          :primary-color="schemaPrimaryColor"
          :show-team-name="showTeamName"
        />
        <h2
          v-else
          class="body-payment-methods-small-amount"
        >
          {{ $t("pay.small-amount") }}
        </h2>
      </el-row>
      <el-row class="body-terms">
        <el-checkbox v-model="termsAgree">
          <i18n path="pay.user-terms">
            <template v-slot:termsLink>
              <a
                class="terms-link"
                href="https://tippie.de/agb/"
                target="_blank"
              >
                {{ $t("pay.user-terms-link") }}
              </a>
            </template>
          </i18n>
        </el-checkbox>
      </el-row>
      <el-row
        type="flex"
        justify="center"
        class="body-certification-logo"
      >
        <img
          src="@/assets/img/cert/SMiG_RGB_150dpi.png"
          alt="SMiG"
        >
        <img
          class="ml-25"
          src="@/assets/img/cert/logo_SHiG.png"
          alt="SHiG"
        >
      </el-row>
      <el-row class="body-logo">
        <el-link
          class="body-logo-image-link"
          :underline="false"
          href="https://tippie.de"
          target="_blank"
        >
          <img
            src="@/assets/img/logo.png"
            alt="Tippie"
          >
        </el-link>
        <el-link
          :underline="false"
          class="body-logo-text-link"
          href="https://tippie.de"
          target="_blank"
        >
          {{ $t("visit-us") }}
        </el-link>
      </el-row>
    </div>
  </div>
  <div
    v-else-if="
      !videoPlayed &&
        paymentInfo &&
        paymentInfo.businessEntity &&
        paymentInfo.businessEntity.presentationMode
    "
    class="video-container"
  >
    <video
      autoplay
      playsinline
      muted
      @ended="finishVideo()"
    >
      <source
        src="https://tippiie-images.s3.eu-central-1.amazonaws.com/operaball.mp4"
        type="video/mp4"
      >
    </video>
  </div>
</template>

<script>
import DefinedTipsAmount from "./Components/DefinedTipsAmount";
import DefinedTipsPercentage from "./Components/DefinedTipsPercentage";
import CurrencyInput from "../Components/CurrencyInput";
import PaymentMethods from "./PaymentMethods/index.vue";
import FeeCheckbox from "./Components/FeeCheckbox";
import PayGuestStrategy from "../../shared/PayGuestStrategy";

import axios from "axios";

export default {
  name: "Pay",
  components: {
    FeeCheckbox,
    DefinedTipsAmount,
    DefinedTipsPercentage,
    CurrencyInput,
    PaymentMethods,
  },
  setup() {
    const payGuest = new PayGuestStrategy();

    return {
      payGuest,
    };
  },
  data: function() {
    return {
      isTermsWarning: false,
      teamMode: false,
      imageURLPath:
        "https://tippiie-images.s3.eu-central-1.amazonaws.com/public/",
      videoPlayed: false,
      fullScreenLoading: true,
      paymentInfo: {
        currency: this.$route.params.currency,
        amount: this.$route.params.amount || 0,
        qrCode: this.$route.params.qrCode,
        feeAmount: 0,
      },
      currencyInputOptions: {
        currency: this.$route.params.currency,
        locale: this.$i18n.locale,
        precision: 2,
        valueRange: {
          min: 0,
          max: 1000,
        },
        valueScaling: "precision",
      },
      termsAgree: true,
      totalAmount: 0,
      showPane: false,
      timeout: null,
      payFee: true,
      minAmount: 50,
    };
  },
  async created() {
    const paramQRCode = Number(this.$route.params.qrCode);

    if (this.isPlanzer) {
      this.termsAgree = false;
    }

    if (
      this.$route.params.currency === "EUR" &&
      paramQRCode >= 2002780 &&
      paramQRCode <= 2002839
    ) {
      window.location.href = window.location.href.replace("EUR", "CHF");
      window.location.reload();
    }

    if (
      process.env.VUE_APP_I18N_SUPPORTED_LOCALE.split(",").includes(
        this.$route.params.locale
      )
    ) {
      this.$i18n.locale = this.$route.params.locale;
    }
    await this.$axios
      .get(`api/v1/teams/qrcodes/${this.paymentInfo.qrCode}`)
      .then(async (response) => {
        const { team, businessEntity } = response.data;

        if (team == null && businessEntity == null) {
          const redirect = await axios
            .get(
              `${process.env.VUE_APP_ADMIN_API_BASE_URL}/api/v1/teams/pools/${this.$route.params.qrCode}`
            )
            .then((res) => {
              if (res.data.teams?.length && res.data.businessEntity != null) {
                this.$router.push({
                  name: "SelectTeamPay",
                  params: {
                    groupQrCode: this.$route.params.qrCode,
                    currency: this.$route.params.currency,
                  },
                });

                return true;
              }
            })
            .catch((e) => console.log(e));
          if (redirect) return;
        }

        if (
          team != null &&
          businessEntity?.groupQrCode > 0 &&
          team.qrCode !== businessEntity?.groupQrCode
        ) {
          this.teamMode = true;
          this.$router
            .replace({
              query: {
                ...this.$route.query,
                mode: "team",
              },
            })
            .catch(() => {});
          this.paymentInfo = { ...this.paymentInfo, ...response.data };
          this.paymentInfo.employee = {
            name: team.name,
            surName: "",
            qrCode: team.qrCode,
            photoUrl: businessEntity.logo,
          };
          if (this.checkRedirect()) return;

          this.checkClientPaysFee();
          this.checkPayFee();
        } else if (
          team != null &&
          (businessEntity?.groupQrCode == null ||
            businessEntity?.groupQrCode === 0)
        ) {
          return this.$router.push({
            name: "SelectEmployee",
            params: {
              qrCode: this.$route.params.qrCode,
              currency: this.$route.params.currency,
            },
          });
        } else {
          await this.$axios
            .get("/api/v1/qrcodes/" + this.paymentInfo.qrCode)
            .then((response) => {
              if (response.data.empAssigned) {
                this.paymentInfo = { ...this.paymentInfo, ...response.data };

                if (this.checkRedirect()) return;

                this.checkClientPaysFee();
                this.checkPayFee();
              } else {
                this.$message.error({
                  message: this.$t("form.item-not-found", { item: "QR Code" }),
                  duration: 0,
                });
              }
            })
            .finally(() => (this.fullScreenLoading = false));
        }
      })
      .finally(() => (this.fullScreenLoading = false));
  },
  mounted() {
    this.$amplitude.logEvent("Payment page event Page loaded", {
      qrCode: this.paymentInfo.qrCode,
    });

    this.$axios.post("/api/v1/fe-status", {
      status: 200,
      message: "Payment page event Page loaded",
      qrCode: this.paymentInfo.qrCode,
      timestamp: Date.now(),
    });
  },
  computed: {
    isPlanzer() {
      return [5000, 2084001].includes(
        Number(this.$route.params.qrCode)
      );
    },
    profileImage() {
      let photo = this.showOrgAvatar
        ? this.paymentInfo.businessEntity.logo
        : this.paymentInfo?.employee?.photoUrl ?? `${this.paymentInfo?.employee.empUid}.png`;

      return `${this.imageURLPath}${photo ||
        "profile.png"}?t=${new Date().getTime()}`;
    },
    showTeamName() {
      return this.paymentInfo.team.showsTeamName || this.teamMode;
    },
    showOrgAvatar() {
      return (
        (this.paymentInfo.team.sharingType === "Pooling" &&
          this.paymentInfo.businessEntity.companyAvatarInsteadOfUser) ||
        this.teamMode
      );
    },
    paymentFor() {
      if (this.paymentInfo.team.showsTeamName || this.teamMode) {
        return this.paymentInfo.team.name;
      }
      return this.paymentInfo.employee.nickName
        ? this.paymentInfo.employee.nickName
        : this.paymentInfo.employee.name;
    },
    schemaPrimaryColor() {
      return this.paymentInfo.businessEntity.schemaPrimaryColor || "#FF8A00";
    },
    totalPay: {
      get: function() {
        if (this.payFee) {
          this.payGuest.setAmount(this.totalAmount);
          return this.payGuest.calculateTotal();
        }

        return this.totalAmount;
      },
    },
    paymentObject: function() {
      return [
        {
          qrCode: this.paymentInfo.qrCode,
          amount: this.totalPay,
          currency: this.$route.params.currency,
        },
      ];
    },
    title() {
      if (this.paymentInfo?.team?.showsTeamName || this.teamMode) {
        return this.paymentInfo.team.name;
      }
      return this.paymentInfo.employee.nickName
        ? this.paymentInfo.employee.nickName
        : `${this.paymentInfo.employee.name} ${this.paymentInfo.employee.surName}`;
    },
  },
  methods: {
    showTermsWarning() {
      if (this.isPlanzer) {
        this.isTermsWarning = true;
      }
    },
    checkClientPaysFee() {
      if (this.paymentInfo.businessEntity.clientPaysFeePricing == null) {
        this.payGuest.setClientPaysFeeNull(true);
        this.payGuest.setFeePercentage(
          this.paymentInfo.businessEntity.feePercentage
        );
      } else {
        const orgFeeParams = this.paymentInfo.businessEntity
          .clientPaysFeePricing;
        const percentageFeeAboveThreshold =
          orgFeeParams.percentageFeeAboveThreshold > 0
            ? orgFeeParams.percentageFeeAboveThreshold
            : this.paymentInfo.businessEntity.feePercentage / 100;
        this.payGuest.setClientPaysFeeNull(false);
        this.payGuest.setFixedFee(orgFeeParams.fixedFee);
        this.payGuest.setThreshold(orgFeeParams.threshold);
        this.payGuest.setPercentageFeeAboveThreshold(
          percentageFeeAboveThreshold
        );
      }
    },
    checkPayFee() {
      if (
        this.paymentInfo.businessEntity.feePercentage <= 0 ||
        this.paymentInfo.businessEntity.waiterPaysFee
      ) {
        this.payFee = false;
      }
    },
    checkRedirect() {
      if (
        this.paymentInfo.businessEntity.newDesignPayment &&
        process.env.VUE_APP_NEW_DESIGN_PAYMENT_URL
      ) {
        const redirectLink =
          process.env.VUE_APP_NEW_DESIGN_PAYMENT_URL +
          "/pay/" +
          this.$route.params.qrCode +
          "/" +
          this.$route.params.currency;

          window.location.href = this.$route.params.amount 
          ? `${redirectLink}/${this.$route.params.amount}` 
          : redirectLink

        return true;
      }
    },
    finishVideo() {
      this.videoPlayed = true;
    },
    inputAmount(value) {
      if (this.timeout !== null) {
        clearTimeout(this.timeout);
      }
      this.timeout = setTimeout(() => {
        this.$amplitude.logEvent("Payment page event Amount change", {
          qrCode: this.paymentInfo.qrCode,
          amount: value,
        });
        this.$axios.post("/api/v1/fe-status", {
          status: 200,
          message: "Payment page event Amount change",
          data: value,
          qrCode: this.paymentInfo.qrCode,
          timestamp: Date.now(),
        });
      }, 1000);
      if (this.payFee) {
        this.payGuest.setAmount(this.totalAmount);
        this.paymentInfo.feeAmount = this.payGuest.calculateFee();
        this.$forceUpdate();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.is-terms-warnings {
  margin-top: 80px;
}

.terms-unchecked-warning {
  max-width: 360px;
  width: 100%;
  height: 100%;
  font-weight: bold;
  margin: 0 auto 0;
  color: #cc3300;
  position: absolute;
  left: 50%;
  top: 20px;
  transform: translateX(-50%);
  z-index: 9999;
}

.video-container {
  text-align: center;
  background: #15264b;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;

  video {
    max-height: 100vh;
    width: 100%;
  }
}

.header {
  background: #ff8a00;
  padding: 24px 12px;

  &-logo {
    margin-right: 30px;

    &-img {
      width: 85px;
      height: 85px;
      border-radius: 50%;
      border: 2px solid #ffffff;
    }
  }

  &-toptitle {
    margin: 3px 0 0 0;
    font-family: "Raleway Bold", serif;
    color: #ffffff;
    font-size: 14px;
    word-break: break-word;
  }

  &-title {
    margin: 0;
    font-family: "Raleway Bold", serif;
    color: #ffffff;
    font-size: 28px;
    font-weight: 900;
    word-break: break-word;
  }

  &-subtitle {
    margin: 0;
    font-family: "Raleway Bold", serif;
    color: #ffffff;
    font-size: 12px;
    font-style: italic;
  }
}

.body {
  padding: 30px 12px;

  &-payment {
    &-methods {
      &-small-amount {
        margin-top: 24px;
        text-align: center;
        color: #2d2e2f;
        font-family: "Raleway Bold", serif;
        font-size: 16px;
      }
    }
  }

  &-total-amount {
    text-align: center;
    margin-top: 25px;
    margin-bottom: 12px;
    font-family: "Raleway Bold", serif;
    font-feature-settings: "pnum" on, "lnum" on;
    font-size: 16px;
    color: #2d2e2f;
  }

  &-amount {
    &-text {
      font-family: "Raleway Bold", serif;
      font-feature-settings: "pnum" on, "lnum" on;
      font-size: 16px;
      color: #2d2e2f;
      text-align: center;
      margin-top: 12px;
    }

    &-input {
      margin-top: 12px;
    }
  }

  &-terms::v-deep {
    max-width: 350px;
    margin: 25px auto 0 auto;

    .el-checkbox {
      display: flex;
      align-items: flex-start;
      width: 100%;

      &__inner {
        width: 16px;
        height: 16px;
        background: #2d2e2f;
        border: none;
        border-radius: 2px;

        &:after {
          height: 8px;
          width: 4px;
          border: 2px solid #ffffff;
          border-left: 0;
          border-top: 0;
          left: 5px;
          top: 2px;
        }
      }

      &__label {
        font-family: "Raleway Medium", serif;
        font-size: 12px;
        line-height: 16px;
        color: #2d2e2f;

        span {
          white-space: break-spaces;
        }

        .terms-link {
          color: #b5b5b5;
          text-decoration: underline;
        }
      }
    }
  }

  &-logo {
    text-align: center;
    display: flex;
    flex-direction: column;
    margin-top: 16px;

    img {
      width: 100px;
      height: 60px;
    }

    a {
      width: max-content;
      margin: 0 auto;

      &.body-logo-text-link {
        margin-top: 10px;
      }
    }
  }

  &-certification-logo {
    margin-top: 10px;

    .el-col {
      @media (max-width: 768px) {
        text-align: center;
        &:nth-child(2n) {
          margin-top: 10px;
        }
      }
    }

    img {
      height: 80px;
    }
  }
}
</style>
<style lang="scss">
.payment-page {
  max-width: 1024px;
  margin: 0 auto;
  .currency-input-clear {
    .el-icon-close::before {
      color: var(--schema-color);
    }
  }
}
</style>
